import React from 'react';
import { API } from '../api';
import { Field, Section, Segmented, Toggle } from './ui';
import PosterGallery from './PosterGallery';
import BriefAssistant from './BriefAssistant';
import GeneratingOverlay, { newJobId } from './GeneratingOverlay';
import PosterDefaults from './PosterDefaults';
import SeasonSheet, { competitionWhen, eventWhen } from './SeasonSheet';

/*
 * Generating event posters for the club screen.
 *
 * Three things shape this screen.
 *
 * Each generation costs the club real money, so what it will cost and how
 * much of the month's allowance is left are on the button, not buried in
 * help text. And it takes the best part of a minute at 4K, so the wait is a
 * state of the screen rather than a spinner that leaves you wondering
 * whether the click registered.
 *
 * Most of what makes a good poster is already in the database. Picking the
 * competition fills in the name, the date, the format and the entry fee, so
 * the admin is left writing the part only they know — what it should feel
 * like. Those facts are shown before generating, because a poster with the
 * wrong date on it has cost real money to get wrong.
 *
 * Generating does not publish. A new poster is off, and an admin turns it on
 * once they have looked at it — a model will occasionally produce something
 * you would not put on the wall.
 */

const SIZE_HINT = {
  '3840x2160': 'Fills a 4K club screen exactly',
  '2048x1152': 'Same shape, smaller and quicker',
  '1536x1024': 'Wider than tall, with space either side on screen',
  '1024x1536': 'Poster shape — good for printing, letterboxed on screen',
  '1024x1024': 'Square',
};

const centsToText = c => (c >= 100 ? `$${(c / 100).toFixed(2)}` : `${c}c`);

/* "Wherever it fits" measures the finished poster and puts the logo on its
   quietest patch, which is somewhere different on every poster — a fixed
   corner is sometimes where the headline turned out to be. The fixed corners
   stay for a club that wants its logo in the same place come what may. */
const LOGO_WHERE = {
  none: 'No logo',
  auto: 'Wherever it fits best',
  'bottom-left': 'Bottom left',
  'bottom-centre': 'Bottom centre',
  'bottom-right': 'Bottom right',
};

/* "auto" is what happens if nobody chooses: the registration code on a
   competition poster, nothing on anything else. The membership codes are
   asked for, never assumed — a quiz night poster should not recruit. */
const QR_LABELS = {
  auto: 'Automatic',
  'membership-join': 'Membership — join',
  'membership-renew': 'Membership — renew',
  none: 'No code',
};

const EMPTY_BRIEF = {
  headline: '', description: '', callToAction: '', whenText: '', feeText: '',
  stylePreset: '', avoid: '', logoId: '', logoPlacement: '', qrMode: 'auto',
  /* undefined means 'whatever the club setting says'; false turns the
     learned style off for this poster alone. */
  useLearnedStyle: undefined,
};


/**
 * Which of the club's logos a poster carries.
 *
 * "The club's default" is always an option and is what a club with one logo
 * gets without choosing. Shown as pictures rather than a dropdown of names,
 * because "Main" and "White version" mean nothing until you see them.
 */
function LogoPicker({ logos, defaultLogo, value, onChange }) {
  if (!logos || logos.length < 2) return null;
  return (
    <div className="logo-pick-row">
      <button type="button" className={`logo-pick${!value ? ' logo-pick-on' : ''}`}
        aria-pressed={!value} onClick={() => onChange('')}>
        {defaultLogo ? <img src={defaultLogo} alt="" /> : <span style={{ height: 40 }} />}
        <span>Club default</span>
      </button>
      {logos.map(l => (
        <button type="button" key={l.id}
          className={`logo-pick${value === l.id ? ' logo-pick-on' : ''}`}
          aria-pressed={value === l.id} onClick={() => onChange(l.id)}>
          <img src={l.data} alt="" />
          <span>{l.label}</span>
        </button>
      ))}
    </div>
  );
}

/* A slot taken or given back. Recomputed rather than re-fetched: the count
   is arithmetic, and a round trip to learn that 19 became 20 is a round trip
   the admin waits for. */
function bumpStorage(storage, by) {
  if (!storage) return storage;
  const stored = Math.max(0, storage.stored + by);
  return { ...storage, stored, remaining: Math.max(0, storage.cap - stored), full: stored >= storage.cap };
}

function PosterStudio({ clubId, clubs, showToast }) {
  const [tab, setTab] = React.useState('create');
  const [config, setConfig] = React.useState(null);
  const [posters, setPosters] = React.useState([]);
  const [photos, setPhotos] = React.useState([]);

  /* One value for both kinds: "c12" or "e3". Two separate dropdowns would
     make the admin answer "which sort of thing is this?" before they can
     answer "which thing is it?", and only one of those is a real question. */
  const [subjectKey, setSubjectKey] = React.useState('');
  const [brief, setBrief] = React.useState(EMPTY_BRIEF);
  const [photoIds, setPhotoIds] = React.useState([]);
  /* A "what's on" card: several events on one poster, for printing and
     leaving on the tables. Kept as its own list rather than as a subject,
     because the poster is not FOR any one of them. */
  const [eventIds, setEventIds] = React.useState([]);
  /* The season calendar's competitions, alongside the events above. Same
     picker, one more list. */
  const [compIds, setCompIds] = React.useState([]);
  const [size, setSize] = React.useState('');
  const [quality, setQuality] = React.useState('');
  /* On, every time the screen opens. A club that overrode the look for one
     poster last Tuesday did so for that poster, and carrying the override
     forward silently is how the next six come out wrong. */
  const [useDefaults, setUseDefaults] = React.useState(true);

  const [facts, setFacts] = React.useState(null);
  const [promptText, setPromptText] = React.useState('');
  const [showPrompt, setShowPrompt] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  /* The job the server is reporting on, and whether its overlay is still up.
     Separate, because dismissing the overlay must not abandon the poster —
     it has been paid for the moment the model draws it. */
  const [job, setJob] = React.useState(null);
  const [watching, setWatching] = React.useState(false);
  const [childBusy, setChildBusy] = React.useState(false);
  /* The poster just generated, so the gallery can open it straight into the
     placer. Where the logo landed is the last thing decided about a poster
     and the only one that costs nothing to change. */
  const [justMade, setJustMade] = React.useState(null);
  const [error, setError] = React.useState('');

  const club = clubs.find(c => String(c.id) === String(clubId));

  const load = React.useCallback(() => {
    if (!clubId) return;
    fetch(`${API}/api/posters/config?clubId=${clubId}`)
      .then(r => (r.ok ? r.json() : null))
      .then(c => {
        setConfig(c);
        // Start from the club's standing answers rather than a hard-coded default.
        if (c && c.settings) { setSize(c.settings.size); setQuality(c.settings.quality); }
      })
      .catch(() => setConfig(null));
    fetch(`${API}/api/posters?clubId=${clubId}&gallery=1`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => setPosters(Array.isArray(d) ? d : [])).catch(() => setPosters([]));
    fetch(`${API}/api/posters/photos?clubId=${clubId}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => setPhotos(Array.isArray(d) ? d : [])).catch(() => setPhotos([]));
  }, [clubId]);

  React.useEffect(load, [load]);

  const isMulti = subjectKey === 'multi';
  /* The season calendar is a list like 'multi', not a subject: nothing on it
     is what the poster is FOR. Both are kept out of subjectId, which would
     otherwise slice an id out of the word itself. */
  const isSeason = subjectKey === 'season';
  const isList = isMulti || isSeason;
  const subjectId = subjectKey && !isList ? subjectKey.slice(1) : '';
  const competitionId = subjectKey.startsWith('c') ? subjectId : '';
  const eventId = subjectKey.startsWith('e') ? subjectId : '';

  const toggleEvent = id => setEventIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleComp = id => setCompIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const competition = config && competitionId
    ? (config.competitions || []).find(c => String(c.id) === String(competitionId))
    : null;
  const event = config && eventId
    ? (config.events || []).find(e => String(e.id) === String(eventId))
    : null;
  const subject = competition || event;

  /* Show the facts that will be printed, before anything is spent. This call
     costs nothing — it composes the prompt without sending it. */
  React.useEffect(() => {
    if (!clubId || !config) return undefined;
    if (!subjectKey && !brief.description.trim() && !brief.headline.trim() && !eventIds.length) {
      setFacts(null); setPromptText(''); return undefined;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      fetch(`${API}/api/posters/preview-prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clubId, competitionId: competitionId || null, eventId: eventId || null,
          eventIds: isMulti ? eventIds : undefined, brief, size, photoIds,
        }),
      })
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (!cancelled && d) { setFacts(d.facts || []); setPromptText(d.prompt || ''); } })
        .catch(() => {});
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [clubId, config, competitionId, eventId, subjectKey, eventIds, brief, size, photoIds]);

  const setB = (k, v) => setBrief(prev => ({ ...prev, [k]: v }));

  const togglePhoto = id => setPhotoIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : (prev.length >= 4 ? prev : [...prev, id]));

  const generate = async () => {
    const id = newJobId();
    setJob(id); setWatching(true);
    setError('');
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/posters/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clubId,
          competitionId: competitionId || null,
          eventId: eventId || null,
          eventIds: isMulti ? eventIds : undefined,
          brief, size, quality, photoIds, jobId: id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'That did not work.'); return; }
      setPosters(prev => [data.poster, ...prev]);
      setConfig(prev => (prev ? {
        ...prev,
        used: data.used, cap: data.cap, remaining: data.remaining,
        storage: data.storage || bumpStorage(prev.storage, 1),
      } : prev));
      setBrief(EMPTY_BRIEF);
      setSubjectKey('');
      setEventIds([]);
      setPhotoIds([]);
      setTab('posters');
      /* Which poster the gallery should open the placer on. The gallery
         remembers which ids it has already opened, so this can simply stay
         set — closing the placer does not reopen it. */
      setJustMade(data.poster.id);
      showToast('Poster made. Put the logo where you want it, then look at it before putting it on the screen.');
    } catch (e) {
      setError(e.message || 'That did not work.');
    } finally {
      setBusy(false);
      setWatching(false);
      setJob(null);
    }
  };

  /* One place for every way a poster changes: the gallery hands back whatever
     the server returned, which may be a new poster, a deletion, or fresh
     allowance figures after a refinement. */
  const onChanged = React.useCallback(payload => {
    if (payload.deleted) {
      setPosters(prev => prev.filter(p => p.id !== payload.deleted));
      setConfig(prev => (prev ? { ...prev, storage: bumpStorage(prev.storage, -1) } : prev));
      return;
    }
    if (payload.poster) {
      setPosters(prev => prev.map(p => (p.id === payload.poster.id ? { ...p, ...payload.poster } : p)));
    }
    if (payload.remaining !== undefined) {
      setConfig(prev => (prev ? { ...prev, used: payload.used, cap: payload.cap, remaining: payload.remaining } : prev));
    }
    if (payload.styleHistory !== undefined) {
      setConfig(prev => (prev ? { ...prev, styleHistory: payload.styleHistory } : prev));
    }
  }, []);

  if (!config) {
    return (
      <div className="gs-panel">
        <Section label="Event posters">
          <p style={{ margin: 0, color: 'var(--gs-text-muted)' }}>Loading…</p>
        </Section>
      </div>
    );
  }

  // Two separate reasons this can be unavailable, and they need different actions.
  if (!config.configured) {
    return (
      <div className="gs-panel">
        <Section label="Event posters">
          <p style={{ margin: 0, color: 'var(--gs-text-muted)' }}>
            Image generation is not set up on this server. Someone with access to the server needs
            to add an <code>OPENAI_API_KEY</code>.
          </p>
        </Section>
      </div>
    );
  }
  if (!config.enabledForClub) {
    return (
      <div className="gs-panel">
        <Section label="Event posters">
          <p style={{ margin: 0, color: 'var(--gs-text-muted)' }}>
            {club ? club.name : 'This club'} does not have poster generation switched on. It is an
            optional extra — a site administrator turns it on under Admin&nbsp;→&nbsp;Clubs.
          </p>
        </Section>
      </div>
    );
  }

  const cost = (config.indicativeCents[size] || {})[quality];
  const noAllowance = config.remaining <= 0;

  const defaultPresetLabel =
    (config.stylePresets.find(p => p.id === config.settings.stylePreset) || {}).label || '—';
  /* What the collapsed switch says it is doing. Written as a sentence rather
     than a row of labels because it is read once, in passing, by somebody
     deciding whether they need to open it at all. */
  const defaultsSummary = [
    defaultPresetLabel,
    (config.sizes[size] || {}).label || size,
    cost != null ? `about ${centsToText(cost)} each` : null,
    (LOGO_WHERE[config.settings.logoPlacement] || 'no logo').toLowerCase(),
  ].filter(Boolean).join(', ') + '.';
  /* Two different limits, and they fail for different reasons. The monthly
     one is how many may be made and resets on its own; the storage one is how
     many may be kept and only moves when somebody deletes. Saying "no room"
     when the truth is "wait until Tuesday" would send an admin looking in the
     wrong place. */
  const storage = config.storage || { stored: 0, cap: 0, full: false };
  const noRoom = !!storage.full;
  const hasSubject = isMulti
    ? eventIds.length > 1
    : (!!subjectKey || !!brief.description.trim() || !!brief.headline.trim());
  const canGenerate = !busy && hasSubject && !noAllowance && !noRoom;

  const allowance = (
    <span style={{ fontSize: 'var(--gs-text-sm)' }}>
      <span style={{ color: noAllowance ? 'var(--gs-action-fill, var(--club-10))' : 'var(--gs-text-muted)' }}>
        {/* A club with no monthly allowance but some credits was told
            "5 of 0 left this month". Say which pool the posters come from. */}
        {!config.cap
          ? `${config.credits || 0} ${config.credits === 1 ? 'credit' : 'credits'} left`
          : `${config.monthlyLeft ?? config.remaining} of ${config.cap} left this month`
            + (config.credits ? ` + ${config.credits} ${config.credits === 1 ? 'credit' : 'credits'}` : '')}
      </span>
      <span style={{ color: 'var(--gs-text-faint)' }}> · </span>
      <span style={{ color: noRoom ? 'var(--gs-action-fill, var(--club-10))' : 'var(--gs-text-muted)' }}>
        keeping {storage.stored} of {storage.cap}
      </span>
    </span>
  );

  return (
    <div className="gs-panel">
      {busy && watching && job && (
        <GeneratingOverlay
          jobId={job}
          endpoint="/api/posters/progress"
          title="Making your poster"
          typical="up to a minute at 4K"
          fallbackSteps={['Composing the brief', 'Drawing the poster', 'Saving it']}
          onDismiss={() => setWatching(false)}
        />
      )}

      {/* Locked, not merely dimmed. The overlay stops the mouse, but a
          keyboard could still tab into the brief behind it and edit a
          request that has already been sent — inert takes the whole panel
          out of reach of both. The overlay sits outside it so its own
          button still works. */}
      <div inert={busy || undefined}>
      <div style={{ marginBottom: 'var(--gs-space-4)' }}>
        <Segmented
          ariaLabel="Poster section"
          options={[
            { value: 'create', label: 'Make one' },
            { value: 'posters', label: `Posters (${posters.length})` },
            { value: 'defaults', label: 'Club defaults' },
          ]}
          value={tab} onChange={setTab} />
      </div>

      {tab === 'create' && (
        <>
          <Section label="What is it for" action={allowance}>
            {/* What it is for, the heading and the date, in one row on a
                screen with room for one.
              *
                No --gs-cols: the grid's own auto-fit is what makes this work
                with a changing number of fields. Picking a competition takes
                the heading and the date away — they come off its record — and
                a fixed three-column template would leave two empty tracks and
                a select stranded at a third of the width. auto-fit collapses
                what is not there, so one field fills the row, two share it,
                three sit side by side, and under 620px they stack. */}
            <div className="gs-field-grid">
              <Field label="What it is for" htmlFor="poster-comp"
                help={subject
                  ? 'Its details go on the poster — you do not need to retype them.'
                  : 'Pick one and the details fill themselves in. Or leave it blank for something else.'}>
                <select id="poster-comp" className="w3-select" value={subjectKey}
                  onChange={e => setSubjectKey(e.target.value)}>
                  <option value="">Something else…</option>
                  {(config.events || []).length > 1 && (
                    <option value="multi">Several events on one poster…</option>
                  )}
                  {((config.events || []).length + (config.competitions || []).length) > 1 && (
                    <option value="season">Season event calendar…</option>
                  )}
                  {(config.competitions || []).length > 0 && (
                    <optgroup label="Competitions">
                      {config.competitions.map(c => (
                        <option key={'c' + c.id} value={'c' + c.id}>
                          {c.name}{c.startDate ? ` — ${String(c.startDate).slice(0, 10)}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {(config.events || []).length > 0 && (
                    <optgroup label="Events">
                      {config.events.map(e => (
                        <option key={'e' + e.id} value={'e' + e.id}>
                          {e.name}{e.startDate ? ` — ${String(e.startDate).slice(0, 10)}` : ''}
                          {e.published ? '' : ' (not published)'}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </Field>

              {/* Both come off a competition's record when one is picked, so
                  they are only asked for when there is nothing to take them
                  from. A list has a heading but no single date. */}
              {(!subjectKey || isMulti) && (
                <Field label="Heading" htmlFor="poster-headline"
                  help={isMulti
                    ? `Optional. Left blank it reads "What's on at ${club ? club.name : 'the club'}".`
                    : 'The words printed large across the poster.'}>
                  <input id="poster-headline" className="w3-input"
                    placeholder={isMulti ? "What's on at the club" : 'Twilight Bowls'}
                    value={brief.headline} onChange={e => setB('headline', e.target.value)} />
                </Field>
              )}
              {!subjectKey && (
                <Field label="When" htmlFor="poster-when" help="Printed exactly as you write it.">
                  <input id="poster-when" className="w3-input" placeholder="Tuesdays from 5:30pm"
                    value={brief.whenText} onChange={e => setB('whenText', e.target.value)} />
                </Field>
              )}
            </div>

            {/* One picker, whichever list is being built. A season calendar
                is the same question as several events on one poster — which
                of these go on it — so it is the same control, with the
                competitions added to it rather than a second list of its
                own further down the form. */}
            {isList && (
              <div style={{ marginTop: 'var(--gs-space-4)' }}>
                {isSeason && (config.competitions || []).length > 0 && (
                  <Field label="Which competitions" htmlFor="poster-comps"
                    help="Listed together under one heading, in date order.">
                    <div id="poster-comps" className="event-tick-list">
                      {(config.competitions || []).map(c => {
                        const on = compIds.includes(c.id);
                        return (
                          <button type="button" key={c.id}
                            className={`event-tick${on ? ' event-tick-on' : ''}`}
                            aria-pressed={on}
                            onClick={() => toggleComp(c.id)}>
                            <span className="event-tick-box" aria-hidden="true">{on ? '✓' : ''}</span>
                            <span className="event-tick-name">{c.name}</span>
                            <span className="event-tick-when">{competitionWhen(c)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                )}
                <Field label="Which events" htmlFor="poster-multi"
                  help={isSeason
                    ? 'Grouped by kind — five market days become a Markets section.'
                    : 'Each one is shown as its name and its date — a card to glance at, not a programme. Tick them in the order you want them listed.'}
                  {...(isSeason ? { style: { marginTop: 'var(--gs-space-4)' } } : {})}>
                  <div id="poster-multi" className="event-tick-list">
                    {(config.events || []).map(ev => {
                      const on = eventIds.includes(ev.id);
                      /* On a season sheet the order is the calendar's, not
                         the order they were ticked in, so a number would be
                         a promise the sheet does not keep. */
                      const pos = isSeason ? '✓' : eventIds.indexOf(ev.id) + 1;
                      return (
                        <button type="button" key={ev.id}
                          className={`event-tick${on ? ' event-tick-on' : ''}`}
                          aria-pressed={on}
                          onClick={() => toggleEvent(ev.id)}>
                          <span className="event-tick-box" aria-hidden="true">{on ? pos : ''}</span>
                          <span className="event-tick-name">
                            {ev.name}
                            {/* An unpublished event is not one members can see.
                                Putting it on a printed card is a fair way to
                                announce it, but it should be a decision rather
                                than a surprise. */}
                            {!ev.published && <span className="event-flag">Not published</span>}
                          </span>
                          <span className="event-tick-when">
                            {isSeason
                              ? eventWhen(ev)
                              : (ev.startDate ? String(ev.startDate).slice(0, 10) : 'no date yet')}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </Field>
                {isMulti && eventIds.length === 1 && (
                  <p className="poster-note">Pick at least two — one event on its own is an ordinary poster.</p>
                )}
                {isSeason && (
                  <div className="poster-actions" style={{ marginTop: 'var(--gs-space-3)' }}>
                    <button type="button" className="w3-button w3-round poster-btn"
                      onClick={() => setCompIds(compIds.length === (config.competitions || []).length
                        ? [] : (config.competitions || []).map(c => c.id))}>
                      {compIds.length === (config.competitions || []).length ? 'No competitions' : 'All competitions'}
                    </button>
                    <button type="button" className="w3-button w3-round poster-btn"
                      onClick={() => setEventIds(eventIds.length === (config.events || []).length
                        ? [] : (config.events || []).map(e => e.id))}>
                      {eventIds.length === (config.events || []).length ? 'No events' : 'All events'}
                    </button>
                  </div>
                )}
              </div>
            )}


            <div style={{ marginTop: 'var(--gs-space-4)' }}>
              <Field label="What should it feel like" htmlFor="poster-desc"
                help="The part only you know. Say the mood, the light, the scene — not the facts above.">
                <textarea id="poster-desc" className="w3-input"
                  style={{ minHeight: 90, resize: 'vertical' }}
                  placeholder="Golden hour on the green, long shadows, calm and inviting."
                  value={brief.description} onChange={e => setB('description', e.target.value)} />
              </Field>
              {/* A poster costs cents and a minute; this costs a fraction of a
                  cent. Getting the brief right here is far cheaper than
                  generating the wrong picture and trying again. */}
              <BriefAssistant
                clubId={clubId}
                competitionId={competitionId || null}
                eventId={eventId || null}
                eventIds={isMulti ? eventIds : undefined}
                settings={{ stylePreset: brief.stylePreset || undefined }}
                brief={brief.description}
                fields={{ headline: brief.headline, callToAction: brief.callToAction }}
                onApply={patch => setBrief(b => ({ ...b, ...patch }))}
                showToast={showToast}
              />
            </div>

            {/* The call to action does not sit here empty any more. It was an
                optional line most posters went without, taking up a field on
                every screen — and an admin who wants one is offered a written
                one by the assistant above, which is a better line than a cold
                empty box tends to produce.
              *
                It reappears the moment there is something in it, because the
                assistant's "Use it" writes here and this text gets printed on
                the poster. Wording that goes on a paid picture with nowhere to
                see or change it would be worse than the clutter. */}
            {brief.callToAction ? (
              <div className="gs-field-grid"
                style={{ '--gs-cols': '1fr', marginTop: 'var(--gs-space-4)' }}>
                <Field label="Call to action" htmlFor="poster-cta"
                  help="Printed as written. Clear it to leave it off.">
                  <input id="poster-cta" className="w3-input"
                    value={brief.callToAction} onChange={e => setB('callToAction', e.target.value)} />
                </Field>
              </div>
            ) : null}
          </Section>

          {/* The season calendar takes over from here: it asks a different
              question — which of the year's fixtures go on one page — and a
              headline and a mood are not the answer to it. */}
          {isSeason && (
            <SeasonSheet
              clubId={clubId} config={config} showToast={showToast} busy={busy}
              compIds={compIds} eventIds={eventIds}
              onMade={d => {
                if (d.poster) setPosters(prev => [d.poster, ...prev]);
                if (d.storage) setConfig(prev => (prev ? { ...prev, storage: d.storage } : prev));
                setTab('posters');
              }}
              canGenerate={!busy && !noAllowance && !noRoom}
              onGenerated={d => setConfig(prev => (prev ? {
                ...prev, used: d.used, cap: d.cap, remaining: d.remaining,
              } : prev))} />
          )}

          {!isSeason && photos.length > 0 && (
            <Section label="Use one of the club’s photos"
              action={<span style={{ fontSize: 'var(--gs-text-sm)', color: 'var(--gs-text-muted)' }}>
                {photoIds.length ? `${photoIds.length} picked` : 'Optional'}
              </span>}>
              <p style={{ marginTop: 0, color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
                Given as reference, not pasted in — the poster is drawn around them. Up to four.
                Each one adds a little to the cost.
              </p>
              <div className="photo-grid photo-grid-pick">
                {photos.map(p => {
                  const picked = photoIds.includes(p.id);
                  return (
                    <button type="button" key={p.id}
                      className={`photo-pick${picked ? ' photo-pick-on' : ''}`}
                      aria-pressed={picked}
                      onClick={() => togglePhoto(p.id)}>
                      <img src={p.imageData} alt={p.label} />
                      <span>{p.label}</span>
                    </button>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Everything here already has a club-wide answer, and on most
              posters the club-wide answer is the one wanted — which meant two
              sections of dropdowns on every screen to change nothing. One
              switch instead, on by default, and the fields appear only for the
              poster that genuinely differs.
            *
              Collapsed, it still says what the settings ARE, and what a
              generation will cost. Hiding controls is fair; hiding the price
              of the button underneath them is not, and the cost used to live
              in the Quality field's help text where collapsing would have
              taken it off the screen altogether. */}
          {!isSeason && (
          <Section label="Look and settings">
            <label className="poster-switch">
              <Toggle
                ariaLabel="Use the club's usual settings for this poster"
                checked={useDefaults}
                onChange={() => setUseDefaults(v => !v)} />
              <span>Use the club’s usual settings</span>
            </label>
            <p className="poster-note" style={{ marginTop: 'var(--gs-space-2)' }}>
              {useDefaults
                ? `${defaultsSummary} Turn this off to change any of it for this poster alone.`
                : 'Changed here, for this poster only. The club’s own defaults are under Club defaults.'}
            </p>
          </Section>
          )}

          {!isSeason && !useDefaults && (
          <Section label="Look">
            <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr' }}>
              <Field label="Style" htmlFor="poster-style"
                help={`Club default is ${defaultPresetLabel}.`}>
                <select id="poster-style" className="w3-select" value={brief.stylePreset}
                  onChange={e => setB('stylePreset', e.target.value)}>
                  <option value="">Club default</option>
                  {config.stylePresets.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </Field>
              {/* Per poster, over the club's own setting. A club that mostly
                  wants its learned look still has the odd poster that should
                  owe nothing to the last twenty. */}
              <Field label="Learned style"
                help={config.settings.useLearnedStyle === false
                  ? 'Switched off for the whole club in Club defaults.'
                  : 'What your displayed posters have in common. Turn it off for a poster that should not look like the others.'}>
                <label className="poster-switch">
                  <Toggle
                    ariaLabel="Use the style learned from posters this club has displayed"
                    checked={brief.useLearnedStyle !== false && config.settings.useLearnedStyle !== false}
                    disabled={config.settings.useLearnedStyle === false}
                    onChange={() => setB('useLearnedStyle', brief.useLearnedStyle === false)} />
                  <span>Use the club’s learned style</span>
                </label>
              </Field>
            </div>
          </Section>
          )}

          {!isSeason && !useDefaults && (
          <Section label="Logo and QR code">
            <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr' }}>
              <Field label="Logo" htmlFor="poster-logo-where"
                help={`Club default is ${LOGO_WHERE[config.settings.logoPlacement] || 'no logo'}. You can drag it anywhere afterwards.`}>
                <select id="poster-logo-where" className="w3-select"
                  value={brief.logoPlacement || config.settings.logoPlacement || 'none'}
                  onChange={e => setB('logoPlacement', e.target.value)}>
                  {(config.logoPlacements || []).map(p => (
                    <option key={p} value={p}>{LOGO_WHERE[p] || p}</option>
                  ))}
                </select>
              </Field>

              <Field label="QR code" htmlFor="poster-qr"
                help={brief.qrMode === 'auto'
                  ? (competition ? 'Entries for this competition.' : 'Nothing — this poster is not for a competition.')
                  : (brief.qrMode === 'none' ? 'No code on this poster.' : 'Takes people to your membership form.')}>
                <select id="poster-qr" className="w3-select" value={brief.qrMode || 'auto'}
                  onChange={e => setB('qrMode', e.target.value)}>
                  {(config.qrModes || ['auto', 'membership-join', 'membership-renew', 'none']).map(m => (
                    <option key={m} value={m}>{QR_LABELS[m] || m}</option>
                  ))}
                </select>
              </Field>
            </div>
            {(brief.logoPlacement || config.settings.logoPlacement) !== 'none'
              && (config.logos || []).length > 1 && (
              <LogoPicker logos={config.logos} defaultLogo={config.defaultLogo}
                value={brief.logoId || ''} onChange={v => setB('logoId', v)} />
            )}
          </Section>
          )}

          {!isSeason && !useDefaults && (
          <Section label="Size and quality">
            <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr' }}>
              <Field label="Shape" htmlFor="poster-size" help={SIZE_HINT[size]}>
                <select id="poster-size" className="w3-select" value={size}
                  onChange={e => setSize(e.target.value)}>
                  {Object.entries(config.sizes).map(([v, x]) => (
                    <option key={v} value={v}>{x.label} — {v}</option>
                  ))}
                </select>
              </Field>
              <Field label="Quality"
                help={cost != null ? `About ${centsToText(cost)} each. Higher quality takes longer.` : undefined}>
                <Segmented ariaLabel="Quality"
                  options={config.qualities.map(q => ({ value: q, label: q.charAt(0).toUpperCase() + q.slice(1) }))}
                  value={quality} onChange={setQuality} />
              </Field>
            </div>
          </Section>
          )}

          {/* What will actually be printed, before any money is spent on it. */}
          {!isSeason && facts && facts.length > 0 && (
            <Section label="What will be on it"
              action={
                <button type="button" className="w3-button w3-round poster-btn"
                  onClick={() => setShowPrompt(v => !v)}>
                  {showPrompt ? 'Hide full instruction' : 'See full instruction'}
                </button>
              }>
              <ul className="poster-facts">
                {facts.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
              {/* The facts above are exactly what gets printed. The
                  instruction below is not the whole of what gets sent: the
                  art direction is written when you generate, from all of
                  this, and writing it on every keystroke would cost money to
                  show you a paragraph you did not ask for. */}
              {showPrompt && (
                <>
                  <pre className="poster-prompt-dump">{promptText}</pre>
                  <p className="poster-note">
                    The look is written out in full when you generate, from the brief
                    and the club’s style. The facts above are printed exactly as shown.
                  </p>
                </>
              )}
            </Section>
          )}

          {error && (
            <div className="w3-panel w3-pale-red w3-leftbar w3-border-red w3-round"
              style={{ padding: '10px 14px' }} role="alert">
              {error}
            </div>
          )}

          {/* The season calendar carries its own buttons — keeping the sheet
              costs nothing, and jazzing it up is a generation. */}
          {!isSeason && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 'var(--gs-space-4)',
            marginTop: 'var(--gs-space-4)', flexWrap: 'wrap',
          }}>
            <button className="w3-button w3-round poster-btn-primary"
              disabled={!canGenerate} onClick={() => generate()}>
              {busy ? 'Making it…' : 'Make the poster'}
            </button>
            <span style={{ color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
              {busy ? 'This takes up to a minute at 4K.'
                : noRoom ? `All ${storage.cap} poster slots are full — delete some under Posters to make room.`
                  : noAllowance ? 'No generations or credits left.'
                    : !hasSubject ? (isMulti ? 'Tick at least two events.' : 'Pick a competition or an event, or say what it should feel like.')
                      : 'It will not go on the screen until you say so.'}
            </span>
          </div>
          )}
        </>
      )}

      {tab === 'posters' && (
        <Section label="Posters" action={allowance}>
          {noRoom && (
            <div className="w3-panel w3-pale-yellow w3-leftbar w3-border-orange">
              <p>
                This club is keeping all {storage.cap} of its posters, so no more can be
                made until some go. Delete the ones it has finished with.
              </p>
            </div>
          )}
          <PosterGallery
            posters={posters} onChanged={onChanged} onBusy={setChildBusy}
            showToast={showToast} remaining={config.remaining}
            openPlacerFor={justMade} />
          {childBusy && (
            <p style={{ color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
              Working — a refinement takes up to a minute.
            </p>
          )}
        </Section>
      )}

      {tab === 'defaults' && (
        <PosterDefaults
          clubId={clubId} config={config} photos={photos} onPhotos={setPhotos}
          showToast={showToast}
          onSaved={d => setConfig(prev => ({ ...prev, settings: d.settings, styleHistory: d.styleHistory }))} />
      )}
      </div>
    </div>
  );
}

export default PosterStudio;
